import React, { useState, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    Paper, Typography, TextField, Button, Chip,
    CircularProgress, Table, TableHead, TableRow, TableSortLabel,
    TableCell, TableBody, TablePagination, Divider, IconButton, Collapse, Tooltip, Popover,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import TreeClient from '../../components/treeClient';
import Axios from 'axios';
import { apiATC } from '../../config';

// ─── Design tokens Google Material ──────────────────────────────────────────
const G = {
    blue:       '#1a73e8',
    blueLight:  '#e8f0fe',
    blueMid:    '#d2e3fc',
    text:       '#202124',
    textSub:    '#5f6368',
    border:     '#dadce0',
    bg:         '#f8f9fa',
    white:      '#ffffff',
    shadow:     '0 1px 2px 0 rgba(60,64,67,.3), 0 1px 3px 1px rgba(60,64,67,.15)',
    shadowSm:   '0 1px 2px rgba(60,64,67,.3)',
    red:        '#d93025',
    redLight:   '#fce8e6',
};

const useStyles = makeStyles(() => ({
    root: {
        padding: '16px 24px',
        backgroundColor: G.bg,
        minHeight: '100vh',
        fontFamily: '"Google Sans", Roboto, sans-serif',
    },

    // ── Header ──
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginBottom: 20,
    },
    headerIcon: {
        width: 36,
        height: 36,
        borderRadius: '50%',
        backgroundColor: G.blueLight,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    pageTitle: {
        fontSize: 22,
        fontWeight: 400,
        color: G.text,
        letterSpacing: '-0.3px',
    },

    // ── Filtres ──
    filterCard: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        marginBottom: 16,
        borderRadius: 8,
        border: `1px solid ${G.border}`,
        backgroundColor: G.white,
        boxShadow: 'none',
    },
    filterLabel: {
        fontSize: 10,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 1.2,
        color: G.textSub,
        marginBottom: 6,
    },
    filterCol: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    },
    treeCol: { minWidth: 220, maxWidth: 220 },
    analyserBtn: {
        height: 36,
        paddingLeft: 20,
        paddingRight: 20,
        fontWeight: 600,
        textTransform: 'none',
        borderRadius: 18,
        fontSize: 13,
        letterSpacing: '0.01em',
        boxShadow: 'none',
        '&:hover': { boxShadow: G.shadowSm },
    },
    agencesBtn: {
        height: 36,
        textTransform: 'none',
        borderRadius: 18,
        fontSize: 13,
        fontWeight: 500,
        borderColor: G.border,
        color: G.text,
        '&:hover': { backgroundColor: G.bg, borderColor: G.textSub },
    },

    // ── Barre résultats ──
    resultsBar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 16px',
        borderBottom: `1px solid ${G.border}`,
        backgroundColor: G.white,
    },
    resultsTitle: {
        fontSize: 13,
        fontWeight: 500,
        color: G.textSub,
    },
    chipsRow: { display: 'flex', gap: 8 },
    chipConso: {
        backgroundColor: G.blueLight,
        color: G.blue,
        fontWeight: 600,
        fontSize: 12,
        height: 24,
    },
    chipQte: {
        backgroundColor: G.redLight,
        color: G.red,
        fontWeight: 600,
        fontSize: 12,
        height: 24,
    },

    // ── Table ──
    tableWrap: {
        borderRadius: 8,
        border: `1px solid ${G.border}`,
        backgroundColor: G.white,
        boxShadow: 'none',
        overflow: 'visible',      // ← ne pas clipper le Collapse
    },
    tableScroll: {
        overflowX: 'auto',
    },
    table: {
        minWidth: 1000,
        borderCollapse: 'separate',
        borderSpacing: 0,
        '& thead th': {
            backgroundColor: G.bg,
            color: G.textSub,
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.8,
            padding: '10px 14px',
            borderBottom: `1px solid ${G.border}`,
            whiteSpace: 'nowrap',
            userSelect: 'none',
        },
        '& tbody td': {
            fontSize: 13,
            padding: '9px 14px',
            color: G.text,
            borderBottom: `1px solid #f1f3f4`,
        },
    },
    rowClickable: {
        cursor: 'pointer',
        transition: 'background .15s',
        '&:hover td': { backgroundColor: '#f6fafe' },
    },
    rowOpen: {
        '& td': { backgroundColor: `${G.blueLight} !important`, borderBottom: 'none' },
    },
    expandCell: { width: 40, padding: '0 4px !important' },
    valConso:  { fontWeight: 700, color: G.blue },
    valQter:   { color: G.textSub, fontSize: 12 },

    // ── Détail ──
    detailCell: {
        padding: '0 !important',
        borderBottom: `2px solid ${G.blueMid}`,
    },
    detailInner: {
        padding: '16px 20px 16px 56px',
        backgroundColor: '#fafcff',
        borderLeft: `3px solid ${G.blue}`,
    },
    detailHeading: {
        fontSize: 12,
        fontWeight: 600,
        color: G.blue,
        textTransform: 'uppercase',
        letterSpacing: 0.8,
        marginBottom: 10,
    },
    detailTable: {
        minWidth: 480,
        '& th': {
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.6,
            color: G.blue,
            backgroundColor: G.blueLight,
            padding: '7px 14px',
            borderBottom: `1px solid ${G.blueMid}`,
        },
        '& td': {
            fontSize: 12,
            padding: '6px 14px',
            color: G.text,
            borderBottom: `1px solid #eef1fb`,
        },
        '& tr:last-child td': { borderBottom: 0 },
    },
    detailLoading: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 0',
    },
    detailEmpty: {
        fontSize: 13,
        color: G.textSub,
        fontStyle: 'italic',
        padding: '4px 0',
    },

    noResult: {
        textAlign: 'center',
        padding: 48,
        color: G.textSub,
        fontSize: 14,
    },
}));

// ─── Détail d'une ligne ───────────────────────────────────────────────────────

const DETAIL_ROWS_PER_PAGE_DEFAULT = 25;

const DETAIL_COLS = [
    { id: 'date_op',    label: 'Date',              align: 'left',  numeric: false },
    { id: 'ref_cv',     label: 'Réf. CV',           align: 'left',  numeric: false },
    { id: 'designation',label: 'Désignation',       align: 'left',  numeric: false },
    { id: 'Nom_agence', label: 'Agence',            align: 'left',  numeric: false },
    { id: 'conso',      label: 'Poids sortant (ct)',align: 'right', numeric: true  },
];

const DetailRow = ({ row, debut, fin, client, open, colCount }) => {
    const classes = useStyles();
    const [detail, setDetail]   = useState(null);
    const [loading, setLoading] = useState(false);
    const [page, setPage]       = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(DETAIL_ROWS_PER_PAGE_DEFAULT);
    const [sortBy,  setSortBy]  = useState('date_op');
    const [sortDir, setSortDir] = useState('desc');
    const fetched = useRef(false);

    const handleSort = (col) => {
        if (sortBy === col) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(col);
            setSortDir('asc');
        }
        setPage(0);
    };

    const sorted = detail ? [...detail].sort((a, b) => {
        let va = a[sortBy], vb = b[sortBy];
        if (DETAIL_COLS.find(c => c.id === sortBy && c.numeric)) {
            va = parseFloat(va) || 0; vb = parseFloat(vb) || 0;
        } else {
            va = (va || '').toString(); vb = (vb || '').toString();
        }
        if (va < vb) return sortDir === 'asc' ? -1 : 1;
        if (va > vb) return sortDir === 'asc' ? 1 : -1;
        return 0;
    }) : [];

    useEffect(() => {
        if (open && !fetched.current) {
            fetched.current = true;
            setLoading(true);
            Axios.post(`${apiATC}/pierre/pierre.php?action=analyse_conso_detail`, {
                id_pier: row.id_pier, debut, fin, client,
            })
                .then(res => setDetail(Array.isArray(res.data) ? res.data : []))
                .catch(() => setDetail([]))
                .finally(() => setLoading(false));
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <TableRow>
            <TableCell colSpan={colCount} className={classes.detailCell}>
                <Collapse in={open} timeout="auto" unmountOnExit>
                    <div className={classes.detailInner}>
                        <div className={classes.detailHeading}>
                            Détail conso — {row.type} &nbsp;·&nbsp; {row.forme} &nbsp;·&nbsp; {row.poids} ct &nbsp;·&nbsp; {row.qualite}
                        </div>

                        {loading && (
                            <div className={classes.detailLoading}>
                                <CircularProgress size={14} style={{ color: G.blue }} />
                                <Typography variant="caption" style={{ color: G.textSub }}>Chargement…</Typography>
                            </div>
                        )}

                        {!loading && detail !== null && detail.length === 0 && (
                            <Typography className={classes.detailEmpty}>Aucun détail disponible.</Typography>
                        )}

                        {!loading && detail && detail.length > 0 && (
                            <div style={{ overflowX: 'auto' }}>
                                <Table size="small" className={classes.detailTable}>
                                    <TableHead>
                                        <TableRow>
                                            {DETAIL_COLS.map(col => (
                                                <TableCell key={col.id} align={col.align} sortDirection={sortBy === col.id ? sortDir : false}>
                                                    <TableSortLabel
                                                        active={sortBy === col.id}
                                                        direction={sortBy === col.id ? sortDir : 'asc'}
                                                        onClick={() => handleSort(col.id)}
                                                        style={{ color: G.blue, whiteSpace: 'nowrap' }}
                                                    >
                                                        {col.label}
                                                    </TableSortLabel>
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {sorted
                                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                            .map((d, i) => (
                                                <TableRow key={page * rowsPerPage + i}>
                                                    <TableCell>{d.date_op}</TableCell>
                                                    <TableCell>{d.ref_cv}</TableCell>
                                                    <TableCell>{d.designation}</TableCell>
                                                    <TableCell>{d.Nom_agence}</TableCell>
                                                    <TableCell align="right"><strong>{d.conso}</strong></TableCell>
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                                <TablePagination
                                    component="div"
                                    count={detail.length}
                                    page={page}
                                    onChangePage={(_, newPage) => setPage(newPage)}
                                    rowsPerPage={rowsPerPage}
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                                    onChangeRowsPerPage={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                                    labelRowsPerPage="Lignes :"
                                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
                                    style={{ borderTop: `1px solid ${G.blueMid}`, minHeight: 40 }}
                                />
                            </div>
                        )}
                    </div>
                </Collapse>
            </TableCell>
        </TableRow>
    );
};

// ─── Ligne principale ─────────────────────────────────────────────────────────

const COL_COUNT = 14;

const MainRow = ({ row, debut, fin, client }) => {
    const classes = useStyles();
    const [open, setOpen] = useState(false);

    const pct = (part) => {
        const total = parseFloat(row.conso_totale);
        if (!total || !parseFloat(part)) return null;
        return <span className={classes.valQter}> ({Math.round((parseFloat(part) / total) * 100)}%)</span>;
    };

    return (
        <>
            <TableRow
                className={`${classes.rowClickable} ${open ? classes.rowOpen : ''}`}
                onClick={() => setOpen(o => !o)}
            >
                <TableCell className={classes.expandCell}>
                    <IconButton size="small" style={{ color: open ? G.blue : G.textSub }}>
                        {open ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                    </IconButton>
                </TableCell>
                <TableCell>{row.type}</TableCell>
                <TableCell>{row.forme}</TableCell>
                <TableCell align="right">{row.poids}</TableCell>
                <TableCell>{row.qualite}</TableCell>
                <TableCell align="right" className={classes.valConso}>{row.conso_totale}</TableCell>
                <TableCell align="right">{row.quantite}</TableCell>
                <TableCell align="right">{row.prix_moyen}</TableCell>
                <TableCell align="right">{row.euro_theorique}</TableCell>
                <TableCell align="right">{row.dollar_theorique}</TableCell>
                <TableCell align="right">{row.conso_t1}{pct(row.conso_t1)}</TableCell>
                <TableCell align="right">{row.conso_t2}{pct(row.conso_t2)}</TableCell>
                <TableCell align="right">{row.conso_t3}{pct(row.conso_t3)}</TableCell>
                <TableCell align="right">{row.conso_t4}{pct(row.conso_t4)}</TableCell>
            </TableRow>
            <DetailRow
                row={row} debut={debut} fin={fin} client={client}
                open={open} colCount={COL_COUNT}
            />
        </>
    );
};

// ─── Composant principal ──────────────────────────────────────────────────────

const AnalyseConso = () => {
    const classes    = useStyles();
    const currentYear = new Date().getFullYear();

    const [client,   setClient]   = useState('');
    const [debut,    setDebut]    = useState(`${currentYear}-01-01`);
    const [fin,      setFin]      = useState(`${currentYear}-12-31`);
    const [dim1Min,  setDim1Min]  = useState('');
    const [dim1Max,  setDim1Max]  = useState('');
    const [data,     setData]     = useState([]);
    const [loading,  setLoading]  = useState(false);
    const [searched, setSearched] = useState(false);
    const [activeFilters, setActiveFilters] = useState({ debut, fin, client: '' });
    const [anchorEl,  setAnchorEl]  = useState(null);
    const treeOpen = Boolean(anchorEl);

    const nbClients  = client ? client.split(',').filter(Boolean).length : 0;
    const totalConso = data.reduce((s, r) => s + parseFloat(r.conso_totale || 0), 0);
    const totalQte   = data.reduce((s, r) => s + parseInt(r.quantite || 0, 10), 0);

    const handleSearch = async () => {
        setLoading(true);
        setSearched(false);
        try {
            const payload = { debut, fin, client };
            if (dim1Min !== '') payload.dim1Min = dim1Min;
            if (dim1Max !== '') payload.dim1Max = dim1Max;
            const res = await Axios.post(`${apiATC}/pierre/pierre.php?action=analyse_conso`, payload);
            setData(Array.isArray(res.data) ? res.data : []);
            setActiveFilters({ debut, fin, client });
        } catch (err) {
            console.error(err);
            setData([]);
        } finally {
            setLoading(false);
            setSearched(true);
        }
    };

    return (
        <div className={classes.root}>

            {/* ── Filtres ── */}
            <Paper className={classes.filterCard} elevation={0}>

                {/* Sélecteur agences via Popover */}
                <Button
                    variant="outlined"
                    size="small"
                    className={classes.agencesBtn}
                    onClick={e => setAnchorEl(e.currentTarget)}
                    endIcon={<KeyboardArrowDownIcon fontSize="small" />}
                >
                    {nbClients > 0 ? `${nbClients} agence${nbClients > 1 ? 's' : ''}` : 'Toutes les agences'}
                </Button>
                <Popover
                    open={treeOpen}
                    anchorEl={anchorEl}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    PaperProps={{ style: { padding: '12px 16px', minWidth: 260, marginTop: 4, border: `1px solid ${G.border}`, boxShadow: G.shadow } }}
                >
                    <TreeClient handleChange={setClient} />
                </Popover>

                <Divider orientation="vertical" flexItem style={{ margin: '4px 8px' }} />

                <TextField type="date" label="Début" value={debut}
                    onChange={e => e.target.value && setDebut(e.target.value)}
                    variant="outlined" size="small" InputLabelProps={{ shrink: true }}
                    style={{ width: 150 }} />

                <TextField type="date" label="Fin" value={fin}
                    onChange={e => e.target.value && setFin(e.target.value)}
                    variant="outlined" size="small" InputLabelProps={{ shrink: true }}
                    style={{ width: 150 }} />

                <Tooltip title="Filtre sur dim1 (poids unitaire). Vide = tout afficher." arrow>
                    <TextField type="number" label="Poids min (ct)" value={dim1Min}
                        onChange={e => setDim1Min(e.target.value)}
                        variant="outlined" size="small" placeholder="ex : 0.10"
                        inputProps={{ step: 0.01, min: 0 }} style={{ width: 140 }} />
                </Tooltip>

                <Tooltip title="Filtre sur dim1 (poids unitaire). Vide = tout afficher." arrow>
                    <TextField type="number" label="Poids max (ct)" value={dim1Max}
                        onChange={e => setDim1Max(e.target.value)}
                        variant="outlined" size="small" placeholder="ex : 0.20"
                        inputProps={{ step: 0.01, min: 0 }} style={{ width: 140 }} />
                </Tooltip>

                <div style={{ flex: 1 }} />

                <Button variant="contained" color="primary" className={classes.analyserBtn}
                    onClick={handleSearch} disabled={loading}
                    startIcon={loading ? <CircularProgress size={15} color="inherit" /> : <SearchIcon />}>
                    Analyser
                </Button>
            </Paper>

            {/* ── Message vide ── */}
            {searched && !loading && data.length === 0 && (
                <div className={classes.noResult}>
                    <Typography>Aucun résultat pour ces critères.</Typography>
                </div>
            )}

            {/* ── Tableau ── */}
            {data.length > 0 && (
                <Paper className={classes.tableWrap} elevation={0}>

                    {/* Barre résumé */}
                    <div className={classes.resultsBar}>
                        <Typography className={classes.resultsTitle}>
                            {data.length} référence{data.length > 1 ? 's' : ''} &nbsp;—&nbsp; cliquez une ligne pour voir le détail
                        </Typography>
                        <div className={classes.chipsRow}>
                            <Chip size="small" className={classes.chipConso}
                                label={`Conso totale : ${totalConso.toFixed(2)} ct`} />
                            <Chip size="small" className={classes.chipQte}
                                label={`Quantité : ${totalQte.toLocaleString()}`} />
                        </div>
                    </div>

                    {/* Table */}
                    <div className={classes.tableScroll}>
                        <Table className={classes.table} size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell className={classes.expandCell} />
                                    <TableCell>Type</TableCell>
                                    <TableCell>Forme</TableCell>
                                    <TableCell align="right">Poids ct</TableCell>
                                    <TableCell>Qualité</TableCell>
                                    <TableCell align="right">Conso ct</TableCell>
                                    <TableCell align="right">Qté</TableCell>
                                    <TableCell align="right">Prix moy. €/ct</TableCell>
                                    <TableCell align="right">€/ct théo.</TableCell>
                                    <TableCell align="right">$/ct théo.</TableCell>
                                    <TableCell align="right">T1</TableCell>
                                    <TableCell align="right">T2</TableCell>
                                    <TableCell align="right">T3</TableCell>
                                    <TableCell align="right">T4</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.map((row, i) => (
                                    <MainRow
                                        key={row.id_pier != null ? row.id_pier : i}
                                        row={row}
                                        debut={activeFilters.debut}
                                        fin={activeFilters.fin}
                                        client={activeFilters.client}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </Paper>
            )}
        </div>
    );
};

export default AnalyseConso;
